import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Table,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import { getToken } from "../../utilities/authHelper";
import {
  fetchSubscriptions,
  fetchPackages,
  fetchProducts,
} from "../../utilities/AdminUtilities";
import { Field, FriendlyDate } from "./AdminUi";

const fetchDashboardsForSubscription = async (subscriptionId) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = getToken();

  const response = await fetch(
    `${apiUrl}/admin/subscription/get_dashboards?subscription_id=${subscriptionId}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const data = await response.json();
  return data.results || [];
};

const fetchLinkedForPackage = async (packageId) => {
  if (!packageId) return { products: [], dashboards: [] };
  const apiUrl = process.env.REACT_APP_API_URL;
  const resp = await fetch(
    `${apiUrl}/admin/package/get_linked_dashboards?package_id=${packageId}`,
    { headers: { Authorization: `Bearer ${getToken()}` } }
  );
  const data = await resp.json();
  return {
    products: data.results?.product_dashboard?.products || [],
    dashboards: data.results?.product_dashboard?.dashboards || [],
  };
};

function SubscriptionDetails() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState([]);

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editable, setEditable] = useState(false);
  const [editedSubscription, setEditedSubscription] = useState({});
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [detailView, setDetailView] = useState("dashboards");

  // Subscription-scoped display data
  const [subscriptionDashboards, setSubscriptionDashboards] = useState([]);

  // Package linkage (no package editing UI)
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [linkDetailsObject, setLinkDetailsObject] = useState("{}");
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await fetchSubscriptions(setSubscriptions);
    const [pkgData, prodData] = await Promise.all([
      fetchPackages(),
      fetchProducts(),
    ]);
    setPackages(pkgData || []);
    setProducts(prodData || []);
    setLoading(false);
  };

  const handleRowClick = async (subscription) => {
    setSelectedSubscription(subscription);
    setEditedSubscription({ ...subscription });
    setShowModal(true);
    setEditable(false);
    setIsNew(false);
    setDetailView("dashboards");

    try {
      // Dashboards for the subscription
      const dashboards = await fetchDashboardsForSubscription(subscription.id);
      setSubscriptionDashboards(dashboards.dashboard_details || []);

      // Linked products (based on the package attached to the subscription)
      const { products: lp } = await fetchLinkedForPackage(subscription.package_id);
      setLinkedProducts(lp);
    } catch (error) {
      console.error("Error loading details:", error);
      setSubscriptionDashboards([]);
      setLinkedProducts([]);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditedSubscription((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const url = isNew
      ? `${apiUrl}/admin/create_or_update_subscription`
      : `${apiUrl}/admin/create_or_update_subscription?subscription_id=${selectedSubscription.id}`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(editedSubscription),
      });

      if (!response.ok) throw new Error("Save failed");

      setShowSuccess(true);
      handleCloseModal();
      loadData();
    } catch (err) {
      console.error(err);
      setShowError(true);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this subscription?"))
      return;
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      const response = await fetch(
        `${apiUrl}/admin/disable_subscription?subscription_id=${selectedSubscription.id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (!response.ok) throw new Error("Delete failed");

      setShowSuccess(true);
      handleCloseModal();
      loadData();
    } catch (err) {
      console.error(err);
      setShowError(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditable(false);
    setSelectedSubscription(null);
    setEditedSubscription({});
    setIsNew(false);
    setSubscriptionDashboards([]);
    setLinkedProducts([]);
    setSelectedProductId("");
    setLinkDetailsObject("{}");
  };

  const handleCreateNew = () => {
    setEditedSubscription({});
    setSelectedSubscription({});
    setShowModal(true);
    setEditable(true);
    setIsNew(true);
    setSubscriptionDashboards([]);
    setLinkedProducts([]);
    setSelectedProductId("");
    setLinkDetailsObject("{}");
  };

  const handleLinkProduct = async () => {
    if (!selectedSubscription?.package_id) {
      alert("This subscription has no package. Please select a package first.");
      return;
    }
    let detailsParsed = {};
    try {
      detailsParsed = linkDetailsObject?.trim()
        ? JSON.parse(linkDetailsObject)
        : {};
    } catch (e) {
      alert("Invalid JSON in Details Object.");
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL;
    setLinking(true);
    try {
      const response = await fetch(`${apiUrl}/admin/link_product_package`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          product_id: selectedProductId,
          package_id: selectedSubscription.package_id,
          details_object: detailsParsed,
        }),
      });
      if (!response.ok) throw new Error("Link failed");

      // Refresh linked products
      const { products: lp } = await fetchLinkedForPackage(
        selectedSubscription.package_id
      );
      setLinkedProducts(lp);
      setSelectedProductId("");
      setLinkDetailsObject("{}");
      setShowSuccess(true);
    } catch (err) {
      console.error(err);
      setShowError(true);
    } finally {
      setLinking(false);
    }
  };

  const handleCancelEdit = () => {
    if (isNew) {
      handleCloseModal();
    } else {
      setEditedSubscription({ ...selectedSubscription });
      setEditable(false);
    }
  };

  const validityText = (v) =>
    v === -1 || v === "-1" ? "Unlimited" : v || v === 0 ? `${v} days` : "—";

  const statusBadge = (status) => {
    if (!status) return "—";
    const v = String(status).toUpperCase();
    return (
      <Badge bg={v === "ACTIVE" ? "success" : v === "INACTIVE" ? "danger" : "secondary"} pill>
        {status}
      </Badge>
    );
  };

  const input = (label, name, type = "text") => (
    <Form.Group className="ep-group">
      <Form.Label className="pv-label">{label}</Form.Label>
      <Form.Control
        type={type}
        name={name}
        value={editedSubscription[name] || ""}
        onChange={handleEditChange}
        disabled={!editable}
      />
    </Form.Group>
  );

  return (
    <>
      {showSuccess && (
        <Alert variant="success" dismissible onClose={() => setShowSuccess(false)}>
          Operation successful.
        </Alert>
      )}
      {showError && (
        <Alert variant="danger" dismissible onClose={() => setShowError(false)}>
          Something went wrong.
        </Alert>
      )}

      <div className="up-toolbar">
        <div className="text-muted small">
          {subscriptions.length} subscription{subscriptions.length === 1 ? "" : "s"}
        </div>
        <Button variant="primary" onClick={handleCreateNew}>
          + Add Subscription
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" size="sm" />
        </div>
      ) : (
        <div className="up-card">
          <Table hover responsive className="up-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Validity</th>
                <th>Members</th>
                <th>GPT Count</th>
                <th>Package</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="up-empty">
                    No subscriptions found.
                  </td>
                </tr>
              ) : (
                subscriptions.map((sub) => (
                  <tr key={sub.id} className="up-row" onClick={() => handleRowClick(sub)}>
                    <td className="up-name">{sub.subscription_name || "—"}</td>
                    <td>
                      {sub.subscription_type ? (
                        <Badge bg="secondary" pill>
                          {sub.subscription_type}
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{statusBadge(sub.status)}</td>
                    <td>{validityText(sub.validity)}</td>
                    <td>{sub.allowed_member_count ?? "—"}</td>
                    <td>{sub.allowed_gpt_usage_count ?? "—"}</td>
                    <td className="up-email pv-mono">{sub.package_id || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered scrollable>
        <Modal.Header closeButton className="pv-header">
          <div className="w-100">
            <div className="pv-eyebrow">
              {isNew ? "New Subscription" : editable ? "Editing Subscription" : "Subscription"}
            </div>
            <div className="pv-title up-title text-break">
              {editedSubscription.subscription_name || (isNew ? "Untitled subscription" : "Subscription")}
            </div>
            {!isNew && selectedSubscription?.status && (
              <div className="pv-active-line">
                <span>Status</span>
                {statusBadge(selectedSubscription.status)}
              </div>
            )}
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="pv-section-title">General</div>
          <Row className="g-3">
            <Col md={6}>{input("Subscription Name", "subscription_name")}</Col>
            <Col md={6}>{input("Subscription Type", "subscription_type")}</Col>
            <Col md={6}>{input("Status", "status")}</Col>
            {!isNew && (
              <Col md={6}>
                <Form.Group className="ep-group">
                  <Form.Label className="pv-label">Package</Form.Label>
                  <Form.Control
                    className="pv-mono"
                    value={selectedSubscription?.package_id || "—"}
                    disabled
                    readOnly
                  />
                </Form.Group>
              </Col>
            )}
          </Row>

          <div className="pv-section-title">Limits</div>
          <Row className="g-3">
            <Col md={4}>{input("Validity (days)", "validity", "number")}</Col>
            <Col md={4}>{input("Allowed Members", "allowed_member_count", "number")}</Col>
            <Col md={4}>{input("Allowed GPT Count", "allowed_gpt_usage_count", "number")}</Col>
          </Row>

          {!isNew && (
            <>
              <div className="up-subhead">
                <div className="up-segment" role="tablist">
                  <button
                    type="button"
                    className={detailView === "dashboards" ? "is-on" : ""}
                    onClick={() => setDetailView("dashboards")}
                  >
                    Dashboards ({subscriptionDashboards.length})
                  </button>
                  <button
                    type="button"
                    className={detailView === "products" ? "is-on" : ""}
                    onClick={() => setDetailView("products")}
                  >
                    Products ({linkedProducts.length})
                  </button>
                </div>
              </div>

              {detailView === "dashboards" &&
                (subscriptionDashboards.length > 0 ? (
                  <div className="sd-list">
                    {subscriptionDashboards.map((dashboard, index) => (
                      <details
                        className="sd-item"
                        key={dashboard.id || `${dashboard.dashboard_name}-${index}`}
                      >
                        <summary>
                          <span className="up-name">
                            {dashboard.dashboard_name || "Unnamed Dashboard"}
                          </span>
                          {statusBadge(dashboard.dashboard_status)}
                        </summary>
                        <div className="pv-grid">
                          <Field label="Type" value={dashboard.data_reference_type} />
                          <Field label="Category" value={dashboard.dashboard_category} />
                          <Field label="Reference" value={dashboard.data_reference} mono />
                          <Field label="Created By" value={dashboard.created_by} />
                          <Field label="Created">
                            <FriendlyDate value={dashboard.creation_timestamp} />
                          </Field>
                          <Field label="Updated">
                            <FriendlyDate value={dashboard.updation_timestamp} />
                          </Field>
                        </div>
                      </details>
                    ))}
                  </div>
                ) : (
                  <div className="up-empty">No dashboards on this subscription.</div>
                ))}

              {detailView === "products" && (
                <>
                  {linkedProducts.length > 0 ? (
                    <div className="sd-list">
                      {linkedProducts.map((product, index) => (
                        <details
                          className="sd-item"
                          key={product.id || `${product.product_name}-${index}`}
                        >
                          <summary>
                            <span className="up-name">{product.product_name}</span>
                            {statusBadge(product.product_status)}
                          </summary>
                          <div className="pv-grid pv-grid-2">
                            <Field label="Type" value={product.product_type} />
                            <Field label="ID" value={product.id} mono />
                          </div>
                        </details>
                      ))}
                    </div>
                  ) : (
                    <div className="up-empty">No products linked yet.</div>
                  )}

                  <div className="pv-section-title">Link a Product to this Package</div>
                  <div className="sd-link">
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group className="ep-group">
                          <Form.Label className="pv-label">Product</Form.Label>
                          <Form.Select
                            value={selectedProductId}
                            onChange={(e) => setSelectedProductId(e.target.value)}
                          >
                            <option value="">Select a product</option>
                            {products.map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.product_name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="ep-group">
                          <Form.Label className="pv-label">Details Object (JSON)</Form.Label>
                          <Form.Control
                            className="pv-mono"
                            value={linkDetailsObject}
                            onChange={(e) => setLinkDetailsObject(e.target.value)}
                            spellCheck={false}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex align-items-center justify-content-between gap-3 mt-3">
                      <div className="text-danger small">
                        {!selectedSubscription?.package_id &&
                          "Select a package for this subscription to enable linking."}
                      </div>
                      <Button
                        variant="outline-primary"
                        onClick={handleLinkProduct}
                        disabled={!selectedProductId || !selectedSubscription?.package_id || linking}
                      >
                        {linking ? "Linking..." : "Link Product"}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="ep-footer">
          {editable ? (
            <>
              <Button variant="outline-secondary" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave}>
                {isNew ? "Create Subscription" : "Save Changes"}
              </Button>
            </>
          ) : (
            <>
              {!isNew && (
                <Button
                  variant="outline-danger"
                  className="me-auto"
                  disabled={editedSubscription?.status === "INACTIVE"}
                  onClick={handleDelete}
                >
                  Disable
                </Button>
              )}
              <Button variant="outline-secondary" onClick={handleCloseModal}>
                Close
              </Button>
              <Button variant="primary" onClick={() => setEditable(true)}>
                Edit
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default SubscriptionDetails;
